/**
 * UID-466 — the roster the compose-page share modal (`RfqShareModal`) offers.
 *
 * THIS IS A HARDCODED STUB, NOT REAL DATA. It exists because the design
 * (`Fictiv Platform.dc.html:4824-4854`, opened from the Request-summary rail's
 * Share button at `:9776` via `openShare('quote','this RFQ')`) shows a
 * people-picker, and there is no tenant people-directory endpoint to build one
 * against:
 *
 *   - no `GET /users` / `/members` / people-search operation exists in the
 *     contract;
 *   - `app_runtime`'s SELECT on `app_user` was REVOKED in migration 0002, so
 *     even a query is not available without a new SECURITY DEFINER function;
 *   - the one identity seam that does exist, `IdentityDirectory.lookup`
 *     (`apps/api/src/modules/workspaces/identity-directory.ts:36`), resolves a
 *     KNOWN array of user ids in batch — it cannot enumerate.
 *
 * ADR-0082 §6 rejected the picker outright for exactly this reason. ADR-0152
 * (this ticket) reverses that only for the SHELL: the modal is real, the names
 * are not. `RfqShareModal`'s "Add N person(s)" grants nothing — see its doc
 * comment. Replacing this const with a real fetch is the whole of the
 * follow-up work, and nothing else in the modal should need to change.
 *
 * Same honest-stub precedent already in this vertical: `RfqShareQuoteModal.tsx`'s
 * `RECENT_RECIPIENTS` (two fabricated "recently sent to" rows, no send endpoint).
 * The first three names are deliberately the SAME people as
 * `verticals/workspaces/mock-data.ts`'s `TEAM_MEMBERS` (Priya Shah, Diego
 * Ramirez, Wei Zhang) so a demo user who has seen the Projects screens meets a
 * consistent cast rather than two disjoint invented companies.
 *
 * Roles are JOB TITLES, matching the design's secondary line and its
 * "Search people by name or role…" placeholder. Note these have no
 * counterpart in the real schema — `WorkspaceMember` carries only
 * `workspaceRole` (`owner`/`member`) and `email` — which is a second reason
 * this list cannot simply be swapped for live data without a contract change.
 */
export interface SharePerson {
  readonly id: string
  readonly name: string
  readonly role: string
}

/** Eight entries: enough that the scrolling list actually overflows and that the
 * search field has something to do (the design's own `sc-for` placeholder hints
 * at 4 rows, but a 4-row list never overflows and would leave both the scroll
 * container and the filter untested by hand). Gate-5 W8: this comment used to
 * call the list "88vh-capped", which is the COMP's cap (`dc.html:4826`) — this
 * app's `.modal` is `max-height: calc(100vh - var(--space-8))` (ui.css).
 *
 * Ids are `stub-` prefixed (Gate-5 W6) so a fabricated identity can never be
 * mistaken for, or transmitted as, a real one. They previously read `user-1`…,
 * which collided exactly with `workspaces/mock-data.ts`'s real-shaped
 * `WorkspaceMember.userId`s — harmless while `onShareWith` only counts them, and
 * a live defect the moment anything forwards a selection. */
export const SHARE_PEOPLE: readonly SharePerson[] = [
  { id: 'stub-user-1', name: 'Priya Shah', role: 'Mechanical Engineer' },
  { id: 'stub-user-2', name: 'Diego Ramirez', role: 'Procurement Lead' },
  { id: 'stub-user-3', name: 'Wei Zhang', role: 'Design Engineer' },
  { id: 'stub-user-4', name: 'Amara Okafor', role: 'Manufacturing Engineer' },
  { id: 'stub-user-5', name: 'Tom Lindqvist', role: 'Quality Engineer' },
  { id: 'stub-user-6', name: 'Sofia Marchetti', role: 'Supply Chain Analyst' },
  { id: 'stub-user-7', name: 'Ravi Iyer', role: 'Program Manager' },
  { id: 'stub-user-8', name: 'Hannah Boyd', role: 'Mechanical Engineer' },
]

/**
 * Case-insensitive substring match over name AND role, mirroring the design's
 * own filter (`dc.html:8804` matches `p.name` then `p.role`). An empty query
 * matches everything rather than nothing, so the list renders in full on open.
 *
 * `WorkspacePage.tsx:82`'s `matchesSearch` is the same idea but NOT identical
 * (it takes varargs and does not `trim()`) — an earlier version of this comment
 * called it identical, which was wrong. Gate-5 S1 flags the pair as the
 * duplicate-twice-then-extract trigger; extracting a shared
 * `matchesSearch(needle, ...haystacks)` util is filed as a follow-up rather than
 * done here, to keep this change frontend-local to the RFQ vertical.
 */
export function filterSharePeople(
  people: readonly SharePerson[],
  query: string,
): readonly SharePerson[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return people
  return people.filter(
    (person) =>
      person.name.toLowerCase().includes(needle) || person.role.toLowerCase().includes(needle),
  )
}
